import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { TermsContent } from "./TermsContent";

/**
 * Blocks the UI with a non-dismissable modal whenever the platform-wide
 * `terms_version` in app_settings does not match the user's
 * profiles.accepted_terms_version. The user must check the box and accept
 * the new terms to continue using Nightspy.
 */
export const TermsReacceptanceGate = () => {
  const { user, isLoading } = useAuth();
  const [requiredVersion, setRequiredVersion] = useState<string | null>(null);
  const [acceptedVersion, setAcceptedVersion] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isLoading || !user) {
      setLoaded(false);
      return;
    }

    let cancelled = false;
    const userId = user.id;

    const fetchTermsState = async () => {
      try {
        const [settingRes, profileRes] = await Promise.all([
          supabase.from("app_settings").select("value").eq("key", "terms_version").maybeSingle(),
          supabase.from("profiles").select("accepted_terms_version").eq("id", userId).maybeSingle(),
        ]);

        if (cancelled) return;

        // value is JSON e.g. "1" - normalize to string
        const rawVersion = settingRes.data?.value;
        const version = typeof rawVersion === "string" ? rawVersion : rawVersion != null ? String(rawVersion) : null;
        setRequiredVersion(version);
        setAcceptedVersion((profileRes.data as any)?.accepted_terms_version ?? null);
        setLoaded(true);
      } catch (err) {
        console.error("Failed to load terms version:", err);
      }
    };

    fetchTermsState();

    // Re-check when tab becomes visible again (catches devices that were
    // already signed in before the terms version was bumped).
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchTermsState();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", fetchTermsState);

    // Periodic re-check every 5 minutes for long-lived sessions.
    const interval = window.setInterval(fetchTermsState, 5 * 60 * 1000);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", fetchTermsState);
      window.clearInterval(interval);
    };
  }, [user, isLoading]);

  const needsAcceptance =
    !!user && loaded && !!requiredVersion && acceptedVersion !== requiredVersion;

  const handleAccept = async () => {
    if (!user || !requiredVersion) return;
    if (!agreed) {
      toast.error("Please confirm you have read and agree to the updated terms");
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          accepted_terms_version: requiredVersion,
          accepted_terms_at: new Date().toISOString(),
        } as any)
        .eq("id", user.id);

      if (error) throw error;

      setAcceptedVersion(requiredVersion);
      toast.success("Thanks for accepting the updated terms");
    } catch (err: any) {
      console.error("Failed to record terms acceptance:", err);
      toast.error(err?.message || "Could not save your acceptance. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!needsAcceptance) return null;

  return (
    <Dialog open onOpenChange={() => { /* not dismissable */ }}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] w-[calc(100%-2rem)] [&>button[aria-label='Close']]:hidden [&>button:has(svg.lucide-x)]:hidden"
        // Disable default close affordances so users can't dismiss
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-left sm:text-left">
          <DialogTitle className="flex items-center gap-2 text-left">
            <ScrollText className="h-5 w-5" />
            Updated Terms and Conditions
          </DialogTitle>
          <DialogDescription className="text-left">
            Our Terms and Conditions have been updated. Please review and accept the new terms to continue using Nightspy.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[55vh] pr-4 border rounded-md p-4">
          <div className="pb-8">
            <TermsContent />
          </div>
        </ScrollArea>

        <div className="flex items-start gap-2 pt-2">
          <Checkbox
            id="terms-reaccept"
            checked={agreed}
            onCheckedChange={(checked) => setAgreed(checked === true)}
            disabled={submitting}
          />
          <label htmlFor="terms-reaccept" className="text-sm leading-snug cursor-pointer">
            I have read and agree to the updated Terms and Conditions.
          </label>
        </div>

        <Button
          className="w-full"
          onClick={handleAccept}
          disabled={!agreed || submitting}
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accept and Continue"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
